import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import PaymentsIcon from "@mui/icons-material/Payments";
import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { tokens } from "../../theme";
import {
  adminReleasePayout,
  fetchAdminBookingDetail,
} from "../../api/adminBookings";

const lifecycleChipSx = (key) => {
  switch (key) {
    case "pending":
      return { bg: alpha("#FF9800", 0.15), color: "#E65100" };
    case "active":
      return { bg: alpha("#2196F3", 0.15), color: "#1565C0" };
    case "completed":
      return { bg: alpha("#4CAF50", 0.15), color: "#2E7D32" };
    case "canceled":
      return { bg: alpha("#F44336", 0.15), color: "#C62828" };
    default:
      return { bg: alpha("#757575", 0.15), color: "#616161" };
  }
};

const formatDt = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const money = (cents, currency = "USD") => {
  if (cents == null) return "—";
  return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
};

const ViewBookingById = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releasing, setReleasing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminBookingDetail(id);
      setDetail(data);
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to load booking", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReleasePayout = async () => {
    const confirm = await Swal.fire({
      title: "Release owner payout?",
      text: "This sends the queued Stripe transfer to the vehicle owner.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Release payout",
    });
    if (!confirm.isConfirmed) return;

    setReleasing(true);
    try {
      await adminReleasePayout(id);
      Swal.fire("Success", "Payout release processed.", "success");
      await load();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box m="20px">
        <Typography>Booking not found.</Typography>
      </Box>
    );
  }

  const { booking, lifecycle, payments, transfers, timeline, summary, actions } =
    detail;
  const lc = lifecycleChipSx(lifecycle?.key);
  const vehicle = booking?.Vehicle;
  const renter = booking?.Renter;
  const seller = vehicle?.Seller;

  return (
    <Box m="20px">
      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/bookings")}
          variant="outlined"
        >
          Back to bookings
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined">
          Refresh
        </Button>
        {actions?.canReleasePayout && (
          <Button
            variant="contained"
            startIcon={<PaymentsIcon />}
            disabled={releasing}
            onClick={handleReleasePayout}
            sx={{ bgcolor: colors.greenAccent?.[500] || "#4CAF50" }}
          >
            {releasing ? "Releasing…" : "Release payout"}
          </Button>
        )}
      </Stack>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          background: "linear-gradient(90deg, #22a3f1 0%, #67baf0 100%)",
        }}
      >
        <Typography variant="h4" color="white" fontWeight="bold">
          Booking #{id}
        </Typography>
        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={lifecycle?.label || summary?.lifecycleLabel}
            sx={{ bgcolor: lc.bg, color: lc.color, fontWeight: 700 }}
          />
          <Chip
            label={`Payment: ${summary?.paymentStatus || "none"}`}
            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
          />
          <Chip
            label={`Payout: ${summary?.payoutStatus || "none"}`}
            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
          />
          {actions?.isRefunded && (
            <Chip label="Refunded" color="warning" variant="filled" />
          )}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Renter
                </Typography>
              </Stack>
              <Typography variant="body2">
                {summary?.renterName || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {summary?.renterEmail || renter?.email}
              </Typography>
              {renter?.id && (
                <Link href={`/renter/${renter.id}`} underline="hover" mt={1}>
                  View renter profile →
                </Link>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <DirectionsCarIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Vehicle & owner
                </Typography>
              </Stack>
              <Typography variant="body2" fontWeight={600}>
                {summary?.vehicleLabel || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Plate: {vehicle?.noPlate || "—"}
              </Typography>
              <Typography variant="body2" mt={1}>
                Owner: {summary?.sellerName || "—"}
              </Typography>
              <Stack direction="row" spacing={2} mt={1}>
                {vehicle?.Id && (
                  <Link href={`/vehicle/${vehicle.Id}`} underline="hover">
                    Vehicle →
                  </Link>
                )}
                {summary?.sellerId && (
                  <Link href={`/seller/${summary.sellerId}`} underline="hover">
                    Seller →
                  </Link>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Trip & pricing
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Start
                  </Typography>
                  <Typography>{formatDt(booking?.StartDate)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    End
                  </Typography>
                  <Typography>{formatDt(booking?.EndDate)}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total price
                  </Typography>
                  <Typography fontWeight="bold">
                    ${booking?.TotalPrice}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Type / status
                  </Typography>
                  <Typography>
                    {booking?.type} / {booking?.status || "—"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Renter payment
              </Typography>
              {payments?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Intent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{p.payment_status}</TableCell>
                        <TableCell>
                          {p.amountTotal} {p.currency || "USD"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, maxWidth: 120 }}>
                          {p.payment_intent_id || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">No payment record</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Owner payout (Stripe Connect)
              </Typography>
              {transfers?.length ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Transfer ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transfers.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell>{t.status}</TableCell>
                        <TableCell>{money(t.amountCents, t.currency)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {t.transferId || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">
                  No payout transfer queued
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <HistoryIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  History & timeline
                </Typography>
              </Stack>
              {timeline?.length ? (
                <Stack spacing={1.5}>
                  {timeline.map((ev, i) => (
                    <Box
                      key={i}
                      sx={{
                        pl: 2,
                        borderLeft: `3px solid ${
                          ev.kind === "error"
                            ? "#f44336"
                            : ev.kind === "success"
                              ? "#4caf50"
                              : ev.kind === "warning"
                                ? "#ff9800"
                                : "#22a3f1"
                        }`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {formatDt(ev.at)}
                      </Typography>
                      <Typography fontWeight={600}>{ev.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ev.detail}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">No events</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewBookingById;
